import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Target,
  Move,
  Copy,
  Plus,
  ArrowDown,
  ArrowUp,
  ArrowRight,
  Replace,
  Code,
  Zap,
  Eye,
  X,
  Check,
  Clock
} from 'lucide-react';
import mediaBrowserCodeGenerator from '../services/mediaBrowserCodeGenerator.js';
import { useToast } from '../hooks/use-toast';

/**
 * Media Drag & Drop System - Advanced drag-and-drop with JavaScript code generation
 * 
 * Provides sophisticated drag-and-drop functionality for media items with visual feedback,
 * intelligent drop zones, and automatic JavaScript code generation for timeline operations.
 */
const MediaDragDropSystem = ({
  children,
  timelineData = {},
  currentTime = 0,
  onCodeGenerated,
  onDropPreview,
  className = ''
}) => {
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItems, setDraggedItems] = useState([]);
  const [dragPreview, setDragPreview] = useState(null);
  
  // Drop state
  const [dropZones, setDropZones] = useState([]);
  const [activeDropZone, setActiveDropZone] = useState(null);
  const [dropPreviewCode, setDropPreviewCode] = useState('');
  
  // Code generation state
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [pendingOperation, setPendingOperation] = useState(null);
  
  // Refs
  const dragOverlayRef = useRef(null);
  const dropZoneRefs = useRef(new Map());
  
  const { toast } = useToast();

  // Initialize drop zones when timeline data changes
  useEffect(() => {
    initializeDropZones();
  }, [timelineData]);

  // Initialize drop zones based on timeline structure
  const initializeDropZones = useCallback(() => {
    const zones = [];
    
    // Timeline start zone
    zones.push({
      id: 'timeline-start',
      type: 'timeline',
      position: 'start',
      displayName: 'Timeline Start',
      description: 'Add to beginning of timeline',
      coordinates: { x: 0, y: 0, width: 100, height: 60 },
      acceptsMultiple: true,
      icon: ArrowUp,
      color: 'blue'
    });

    // Timeline end zone
    zones.push({
      id: 'timeline-end',
      type: 'timeline',
      position: 'end',
      displayName: 'Timeline End',
      description: 'Add to end of timeline',
      coordinates: { x: timelineData.duration || 0, y: 0, width: 100, height: 60 },
      acceptsMultiple: true,
      icon: ArrowDown,
      color: 'green'
    });

    // Playhead zone
    zones.push({
      id: 'playhead',
      type: 'playhead',
      position: currentTime,
      displayName: 'Playhead',
      description: 'Insert at current time',
      coordinates: { x: currentTime, y: 0, width: 20, height: 60 },
      acceptsMultiple: true,
      icon: Target,
      color: 'orange'
    });

    // Clip zones for replacement
    if (timelineData.clips) {
      timelineData.clips.forEach(clip => {
        zones.push({
          id: `clip-${clip.id}`,
          type: 'clip',
          clipId: clip.id,
          position: clip.startTime,
          displayName: clip.name,
          description: 'Replace this clip',
          coordinates: { 
            x: clip.startTime, 
            y: 0, 
            width: clip.duration, 
            height: 40 
          },
          acceptsMultiple: false,
          icon: Replace,
          color: 'red',
          clipData: clip
        });

        // Add before/after zones for each clip
        zones.push({
          id: `before-${clip.id}`,
          type: 'position',
          position: clip.startTime,
          displayName: `Before ${clip.name}`,
          description: 'Insert before this clip',
          coordinates: { 
            x: clip.startTime - 10, 
            y: 0, 
            width: 10, 
            height: 60 
          },
          acceptsMultiple: true,
          icon: ArrowRight,
          color: 'purple',
          targetClipId: clip.id
        });

        zones.push({
          id: `after-${clip.id}`,
          type: 'position',
          position: clip.startTime + clip.duration,
          displayName: `After ${clip.name}`,
          description: 'Insert after this clip',
          coordinates: { 
            x: clip.startTime + clip.duration, 
            y: 0, 
            width: 10, 
            height: 60 
          },
          acceptsMultiple: true,
          icon: ArrowRight,
          color: 'purple',
          targetClipId: clip.id
        });
      });
    }

    // Gap zones for insertion
    const gaps = findTimelineGaps();
    gaps.forEach((gap, index) => {
      zones.push({
        id: `gap-${index}`,
        type: 'gap',
        position: gap.start,
        displayName: `Gap ${index + 1}`,
        description: `Fill ${formatDuration(gap.duration)} gap`,
        coordinates: { 
          x: gap.start, 
          y: 0, 
          width: gap.duration, 
          height: 60 
        },
        acceptsMultiple: true,
        icon: Plus,
        color: 'yellow',
        gapData: gap
      });
    });

    setDropZones(zones);
  }, [timelineData, currentTime]);

  // Find gaps in timeline
  const findTimelineGaps = useCallback(() => {
    const gaps = [];
    if (!timelineData.clips || timelineData.clips.length === 0) return gaps;

    const sortedClips = [...timelineData.clips].sort((a, b) => a.startTime - b.startTime);
    
    for (let i = 0; i < sortedClips.length - 1; i++) {
      const currentEnd = sortedClips[i].startTime + sortedClips[i].duration;
      const nextStart = sortedClips[i + 1].startTime;
      
      if (nextStart > currentEnd + 0.1) { // 0.1s minimum gap
        gaps.push({
          start: currentEnd,
          end: nextStart,
          duration: nextStart - currentEnd
        });
      }
    }
    
    return gaps;
  }, [timelineData.clips]);

  // Handle drag start
  const handleDragStart = useCallback((mediaItems, event) => {
    const items = Array.isArray(mediaItems) ? mediaItems : [mediaItems];
    
    setIsDragging(true);
    setDraggedItems(items);
    
    // Create drag preview
    const preview = {
      items,
      count: items.length,
      totalDuration: items.reduce((sum, item) => sum + item.duration, 0),
      thumbnails: items.slice(0, 3).map(item => item.thumbnail).filter(Boolean)
    };
    
    setDragPreview(preview);
    
    // Set drag data for external drops
    if (event?.dataTransfer) {
      event.dataTransfer.setData('application/json', JSON.stringify({
        type: 'media-items',
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          duration: item.duration,
          tags: item.tags
        }))
      }));
      event.dataTransfer.effectAllowed = 'all';
    }
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setDraggedItems([]);
    setDragPreview(null);
    setActiveDropZone(null);
    setDropPreviewCode('');
  }, []);

  // Handle drag over drop zone
  const handleDragOver = useCallback((dropZone, event) => {
    if (event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = dropZone.acceptsMultiple || draggedItems.length === 1 ? 'copy' : 'none';
    }
    
    if (activeDropZone?.id !== dropZone.id) {
      setActiveDropZone(dropZone);
      
      // Generate preview code
      if (draggedItems.length > 0) {
        generateDropPreviewCode(draggedItems, dropZone);
      }
    }
  }, [draggedItems, activeDropZone]);

  // Handle drop
  const handleDrop = useCallback(async (dropZone, event) => {
    if (event) {
      event.preventDefault();
    }
    
    if (!draggedItems.length || !dropZone) return;
    
    // Check if drop is allowed
    if (!dropZone.acceptsMultiple && draggedItems.length > 1) {
      toast({
        title: "Drop Not Allowed",
        description: "This drop zone only accepts single items",
        variant: "destructive"
      });
      handleDragEnd();
      return;
    }
    
    // Generate operation code
    const operation = generateDropOperation(draggedItems, dropZone);
    
    if (operation) {
      setPendingOperation(operation);
      setShowCodePreview(true);
    }
    
    handleDragEnd();
  }, [draggedItems, toast]);

  // Generate preview code for drop operation
  const generateDropPreviewCode = useCallback((items, dropZone) => {
    try {
      const operation = generateDropOperation(items, dropZone);
      if (operation) {
        setDropPreviewCode(operation.code);
        onDropPreview?.(operation);
      }
    } catch (error) {
      console.error('Failed to generate drop preview:', error);
    }
  }, [onDropPreview]);

  // Generate drop operation based on drop zone
  const generateDropOperation = useCallback((items, dropZone) => {
    try {
      switch (dropZone.type) {
        case 'timeline':
          return mediaBrowserCodeGenerator.generateAddToTimelineCode(items, {
            position: dropZone.position,
            targetTime: dropZone.position === 'start' ? 0 : undefined
          });
          
        case 'playhead':
          return mediaBrowserCodeGenerator.generateInsertAtPlayheadCode(items, currentTime, {
            splitExisting: true,
            pushClips: true
          });
          
        case 'clip':
          return mediaBrowserCodeGenerator.generateReplaceClipCode(items[0], dropZone.clipId, {
            preserveTiming: true,
            adjustDuration: 'auto'
          });
          
        case 'position':
          return mediaBrowserCodeGenerator.generateAddToTimelineCode(items, {
            position: dropZone.position === 'before' ? 'before' : 'after',
            targetClipId: dropZone.targetClipId
          });
          
        case 'gap':
          return mediaBrowserCodeGenerator.generateInsertAtPlayheadCode(items, dropZone.gapData.start, {
            createGap: false,
            pushClips: false
          });
          
        default:
          return mediaBrowserCodeGenerator.generateAddToTimelineCode(items);
      }
    } catch (error) {
      console.error('Failed to generate drop operation:', error);
      return null;
    }
  }, [currentTime]);

  // Execute pending operation
  const executePendingOperation = useCallback(async () => {
    if (!pendingOperation) return;
    
    try {
      await onCodeGenerated?.(pendingOperation.code, `Drop: ${pendingOperation.operation}`);
      
      toast({
        title: "Drop Operation Executed",
        description: `Successfully executed: ${pendingOperation.operation}`,
      });
      
      setShowCodePreview(false);
      setPendingOperation(null);
    } catch (error) {
      toast({
        title: "Execution Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  }, [pendingOperation, onCodeGenerated, toast]);

  // Format duration helper
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  return (
    <div className={`relative ${className}`}>
      {children}
      
      {/* Drag & Drop Overlay */}
      {isDragging && (
        <DragDropOverlay
          dragPreview={dragPreview}
          dropZones={dropZones}
          activeDropZone={activeDropZone}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          formatDuration={formatDuration}
        />
      )}
      
      {/* Drop Preview Code */}
      {isDragging && dropPreviewCode && activeDropZone && (
        <DropPreviewPanel
          dropZone={activeDropZone}
          previewCode={dropPreviewCode}
          draggedItems={draggedItems}
          formatDuration={formatDuration}
        />
      )}
      
      {/* Code Preview Modal */}
      {showCodePreview && pendingOperation && (
        <CodePreviewModal
          operation={pendingOperation}
          onExecute={executePendingOperation}
          onCancel={() => {
            setShowCodePreview(false);
            setPendingOperation(null);
          }}
        />
      )}
    </div>
  );
};

// Drag & Drop Overlay Component
const DragDropOverlay = ({ 
  dragPreview, 
  dropZones, 
  activeDropZone, 
  onDragOver, 
  onDrop,
  formatDuration 
}) => {
  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Drop Zones */}
      <div className="absolute inset-0">
        {dropZones.map(zone => (
          <DropZone
            key={zone.id}
            zone={zone}
            isActive={activeDropZone?.id === zone.id}
            onDragOver={onDragOver}
            onDrop={onDrop}
          />
        ))}
      </div>
      
      {/* Drag Preview */}
      {dragPreview && (
        <DragPreview
          preview={dragPreview}
          formatDuration={formatDuration}
        />
      )}
    </div>
  );
};

// Drop Zone Component
const DropZone = ({ zone, isActive, onDragOver, onDrop }) => {
  const IconComponent = zone.icon;
  
  return (
    <div
      className={`absolute pointer-events-auto transition-all duration-200 ${
        isActive ? 'scale-110 z-10' : 'z-0'
      }`}
      style={{
        left: `${zone.coordinates.x}px`,
        top: `${zone.coordinates.y}px`,
        width: `${zone.coordinates.width}px`,
        height: `${zone.coordinates.height}px`,
      }}
      onDragOver={(e) => onDragOver(zone, e)}
      onDrop={(e) => onDrop(zone, e)}
    >
      <div className={`w-full h-full rounded-lg border-2 border-dashed transition-all ${
        isActive 
          ? `border-${zone.color}-400 bg-${zone.color}-400 bg-opacity-20` 
          : `border-${zone.color}-600 bg-${zone.color}-600 bg-opacity-10`
      }`}>
        <div className="flex flex-col items-center justify-center h-full p-2">
          <IconComponent className={`w-4 h-4 ${
            isActive ? `text-${zone.color}-300` : `text-${zone.color}-500`
          }`} />
          <span className={`text-xs font-medium mt-1 ${
            isActive ? `text-${zone.color}-200` : `text-${zone.color}-400`
          }`}>
            {zone.displayName}
          </span>
        </div>
      </div>
    </div>
  );
};

// Drag Preview Component
const DragPreview = ({ preview, formatDuration }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  return (
    <div
      className="absolute z-20 pointer-events-none"
      style={{
        left: mousePosition.x + 10,
        top: mousePosition.y + 10,
        transform: 'translate(-50%, -50%)'
      }}
    >
      <Card className="bg-zinc-800 border-zinc-600 p-3 shadow-lg">
        <div className="flex items-center space-x-3">
          {/* Thumbnails */}
          <div className="flex -space-x-2">
            {preview.thumbnails.slice(0, 3).map((thumbnail, index) => (
              <div
                key={index}
                className="w-8 h-8 rounded bg-zinc-700 border border-zinc-600 overflow-hidden"
                style={{ zIndex: 10 - index }}
              >
                {thumbnail && (
                  <img
                    src={thumbnail}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            ))}
          </div>
          
          {/* Info */}
          <div>
            <div className="text-white text-sm font-medium">
              {preview.count === 1 ? preview.items[0].name : `${preview.count} items`}
            </div>
            <div className="text-gray-400 text-xs">
              {formatDuration(preview.totalDuration)}
            </div>
          </div>
          
          {/* Drag indicator */}
          <div className="text-blue-400">
            <Move className="w-4 h-4" />
          </div>
        </div>
      </Card>
    </div>
  );
};

// Drop Preview Panel Component
const DropPreviewPanel = ({ 
  dropZone, 
  previewCode, 
  draggedItems, 
  formatDuration 
}) => {
  const IconComponent = dropZone.icon;
  
  return (
    <div className="fixed bottom-4 right-4 z-30 pointer-events-none">
      <Card className="bg-zinc-800 border-zinc-600 p-4 shadow-lg max-w-sm">
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded bg-${dropZone.color}-600 bg-opacity-20`}>
            <IconComponent className={`w-4 h-4 text-${dropZone.color}-400`} />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-medium">
              {dropZone.displayName}
            </div>
            <div className="text-gray-400 text-xs mb-2">
              {dropZone.description}
            </div>
            
            {/* Items summary */}
            <div className="text-xs text-gray-300 mb-2">
              {draggedItems.length === 1 
                ? draggedItems[0].name 
                : `${draggedItems.length} items`
              } • {formatDuration(draggedItems.reduce((sum, item) => sum + item.duration, 0))}
            </div>
            
            {/* Code preview */}
            <div className="bg-zinc-900 rounded p-2 text-xs text-gray-300 font-mono overflow-hidden">
              <div className="flex items-center space-x-1 mb-1">
                <Code className="w-3 h-3 text-blue-400" />
                <span className="text-blue-300">Generated Code:</span>
              </div>
              <div className="max-h-20 overflow-y-auto">
                {previewCode.split('\n').slice(0, 4).map((line, index) => (
                  <div key={index} className="truncate">
                    {line}
                  </div>
                ))}
                {previewCode.split('\n').length > 4 && (
                  <div className="text-gray-500">...</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Code Preview Modal Component
const CodePreviewModal = ({ operation, onExecute, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-3xl bg-zinc-800 border-zinc-600 max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Move className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">
                Drop Operation: {operation.operation}
              </h3>
            </div>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-96">
          <div className="mb-4">
            <label className="text-sm text-gray-300 mb-2 block">
              Generated JavaScript Code:
            </label>
            <pre className="bg-zinc-900 border border-zinc-600 rounded p-3 text-sm text-gray-100 overflow-x-auto">
              {operation.code}
            </pre>
          </div>
          
          {operation.metadata && (
            <div className="mb-4">
              <label className="text-sm text-gray-300 mb-2 block">
                Operation Details:
              </label>
              <div className="bg-zinc-900 border border-zinc-600 rounded p-3 text-sm">
                <div className="grid grid-cols-2 gap-2 text-gray-300">
                  <div>Operation: {operation.operation}</div>
                  <div>Timestamp: {new Date(operation.timestamp).toLocaleTimeString()}</div>
                  {operation.metadata.mediaItems && (
                    <div className="col-span-2">
                      Items: {operation.metadata.mediaItems.map(item => item.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-zinc-700 flex items-center justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onExecute} className="bg-blue-600 hover:bg-blue-700">
            <Zap className="w-4 h-4 mr-2" />
            Execute Drop Operation
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Hook for making components draggable
export const useDraggable = (mediaItems, options = {}) => {
  const [isDragging, setIsDragging] = useState(false);
  
  const dragProps = {
    draggable: true,
    onDragStart: (event) => {
      setIsDragging(true);
      options.onDragStart?.(mediaItems, event);
    },
    onDragEnd: (event) => {
      setIsDragging(false);
      options.onDragEnd?.(event);
    },
    className: `${options.className || ''} ${isDragging ? 'opacity-50' : ''}`
  };
  
  return { dragProps, isDragging };
};

// Hook for making components droppable
export const useDroppable = (dropZone, options = {}) => {
  const [isOver, setIsOver] = useState(false);
  
  const dropProps = {
    onDragOver: (event) => {
      event.preventDefault();
      setIsOver(true);
      options.onDragOver?.(dropZone, event);
    },
    onDragLeave: (event) => {
      setIsOver(false);
      options.onDragLeave?.(event);
    },
    onDrop: (event) => {
      event.preventDefault();
      setIsOver(false);
      options.onDrop?.(dropZone, event);
    },
    className: `${options.className || ''} ${isOver ? 'ring-2 ring-blue-400' : ''}`
  };
  
  return { dropProps, isOver };
};

export default MediaDragDropSystem;